import { useState } from 'react';

import { FileSearch, History } from 'lucide-react';

import { humanizeLabel } from '@/nucleo/displayLabels';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import {
  useHistoricoDeInscricaoDoCredenciado,
  useHistoricoDoCredenciado,
  type EventoDeHistoricoDoCredenciado,
} from './detalheDados';

interface AbaHistoricoProps {
  credenciadoId: string;
}

function corDaAcao(
  acao: EventoDeHistoricoDoCredenciado['acao'],
): 'default' | 'destructive' | 'outline' {
  if (acao === 'INSERT') return 'default';
  if (acao === 'DELETE') return 'destructive';
  return 'outline';
}

/**
 * Aba "Histórico" — trilha de auditoria do cadastro, com um atalho para o
 * histórico da inscrição original (versão enxuta do `InscricaoHistoricoModal`
 * do protótipo, D12).
 */
export function AbaHistorico({ credenciadoId }: AbaHistoricoProps) {
  const { data: eventos, isLoading } = useHistoricoDoCredenciado(credenciadoId);
  const [modalAberto, setModalAberto] = useState(false);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm">Alterações no cadastro</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={() => setModalAberto(true)}
        >
          <FileSearch className="h-3.5 w-3.5" aria-hidden="true" />
          Ver histórico de inscrição
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <Spinner size="sm" />
        ) : !eventos || eventos.length === 0 ? (
          <EmptyState
            icon={History}
            title="Sem histórico"
            description="O histórico de alterações será exibido aqui."
          />
        ) : (
          <div className="space-y-4">
            {eventos.map((evento) => (
              <div key={evento.id} className="border-primary/20 flex gap-3 border-l-2 py-2 pl-4">
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <Badge variant={corDaAcao(evento.acao)} className="text-xs">
                      {evento.acao}
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      {formatarDataHora(evento.created_at)}
                    </span>
                  </div>
                  {evento.acao === 'INSERT' ? (
                    <p className="text-muted-foreground text-sm">Registro criado</p>
                  ) : evento.campos_alterados.length > 0 ? (
                    <p className="text-muted-foreground text-sm">
                      Campos alterados: {evento.campos_alterados.map(humanizeLabel).join(', ')}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <HistoricoDeInscricaoModal
        open={modalAberto}
        onOpenChange={setModalAberto}
        credenciadoId={credenciadoId}
      />
    </Card>
  );
}

function HistoricoDeInscricaoModal({
  open,
  onOpenChange,
  credenciadoId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credenciadoId: string;
}) {
  const { data: inscricao, isLoading } = useHistoricoDeInscricaoDoCredenciado(
    open ? credenciadoId : undefined,
  );

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Histórico da inscrição"
      description="Eventos da inscrição original que deu origem a este credenciado."
    >
      {isLoading || !inscricao ? (
        <Spinner size="sm" />
      ) : (
        <div className="space-y-4">
          <div className="bg-muted/30 space-y-1 rounded-lg border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold">{inscricao.protocolo}</span>
              <Badge variant="secondary" className="text-[10px]">
                {inscricao.status_inscricao}
              </Badge>
            </div>
            <p className="text-muted-foreground text-xs">
              Enviada em {formatarDataHora(inscricao.data_envio)} · Analisada em{' '}
              {formatarDataHora(inscricao.data_analise)}
            </p>
            {inscricao.parecer && (
              <p className="text-xs">
                <span className="text-muted-foreground">Parecer:</span> {inscricao.parecer}
              </p>
            )}
          </div>

          {inscricao.eventos.length === 0 ? (
            <EmptyState
              icon={History}
              title="Sem eventos de auditoria"
              description="Não há registros de alteração para esta inscrição."
            />
          ) : (
            <ol className="space-y-2">
              {inscricao.eventos.map((evento) => (
                <li key={evento.id} className="rounded-lg border p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Badge variant={corDaAcao(evento.acao)} className="text-[10px]">
                      {evento.acao}
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      {formatarDataHora(evento.created_at)}
                    </span>
                  </div>
                  {evento.acao === 'INSERT' ? (
                    <p className="text-muted-foreground text-xs">Inscrição criada</p>
                  ) : evento.campos_alterados.length > 0 ? (
                    <p className="text-muted-foreground text-xs">
                      <span className="text-foreground font-medium">Campos:</span>{' '}
                      {evento.campos_alterados.map(humanizeLabel).join(', ')}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </ResponsiveModal>
  );
}
